import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import type { PermissionCode } from '@/constants/permissions';
import { permissionsQueryOptions } from '../api/permissions.api';
import { useAuthStore } from '@/store/auth.store';

/**
 * What the signed-in caller may do, read from the store rather than fetched, so
 * a menu of 88 entries and a screen full of buttons cost no requests.
 *
 * Hiding is not enforcement. Every code checked here is checked again by the
 * endpoint the button calls; this only decides what is worth showing.
 */
export function usePermissions() {
  const permissions = useAuthStore((state) => state.permissions);
  const isAdmin = useAuthStore((state) => state.isAdmin);

  return {
    permissions,
    isAdmin,
    /** An admin user type holds everything, the way the guard treats it. */
    can: (code: PermissionCode | undefined) =>
      code === undefined || isAdmin || permissions.includes(code),
    canAny: (codes: PermissionCode[]) =>
      codes.length === 0 || isAdmin || codes.some((code) => permissions.includes(code)),
  };
}

/**
 * Loads this session's codes into the store. Mounted once, in AppLayout, which
 * covers both signing in and reloading the page -- every signed-in route is
 * inside it.
 */
export function usePermissionsSync() {
  const setPermissions = useAuthStore((state) => state.setPermissions);
  const { data } = useQuery(permissionsQueryOptions());

  useEffect(() => {
    if (data) setPermissions(data.permissions, data.isAdmin);
  }, [data, setPermissions]);
}
